import { useLayoutEffect, useRef } from "react";

const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Через сколько после начала роста проявляется содержимое — как в наборе v6. */
const REVEAL_MS = 110;

/** Малая капля у точки открытия — у вложенного меню, которому кнопки нет. */
const SEED = 44;

/*
 * Размер кнопки Radix пишет на обёртку всплывающего
 * (`--radix-popper-anchor-*`), когда рассчитал положение, — то есть кадром-
 * двумя позже монтажа.
 */
function anchorSize(box: HTMLElement): [number, number] | null {
  const css = getComputedStyle(box.parentElement ?? box);
  const w = parseFloat(css.getPropertyValue("--radix-popper-anchor-width")) || 0;
  const h = parseFloat(css.getPropertyValue("--radix-popper-anchor-height")) || 0;
  return w > 1 && h > 1 ? [w, h] : null;
}

/**
 * Подложка всплывающего, которая вырастает из того, что его открыло, — `menu`
 * и `select` набора v6 без стекла движка (вариант «Б» из проб без стекла):
 * материал «Почты», от размера кнопки у её угла пружиной до всего
 * всплывающего, при закрытии — обратно в кнопку.
 *
 * Ставится первым ребёнком во всплывающее с классом `pop-grow`. Ждёт, пока
 * Radix скажет размер кнопки (не дольше трёх кадров), кладёт его в
 * `--grow-w0`/`--grow-h0` и пускает рост (`data-go`); содержимое — через
 * 110 мс (`data-shown`). Сами движения — правила `pop-grow` в `ui.css`.
 */
export function GrowLayer({
  from = "trigger",
}: {
  /** `point` — малая капля у точки открытия, даже если у меню есть кнопка (вложенное меню). */
  from?: "trigger" | "point";
}) {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const layer = ref.current!;
    const box = layer.parentElement!;
    let frame = 0;
    let reveal = 0;
    let tries = 0;

    const show = () => {
      clearTimeout(reveal);
      reveal = window.setTimeout(
        () => box.dataset.state === "open" && (box.dataset.shown = ""),
        reduced() ? 0 : REVEAL_MS,
      );
    };

    const start = () => {
      const size = from === "point" ? null : anchorSize(box);
      if (!size && from === "trigger" && tries++ < 3) {
        frame = requestAnimationFrame(start);
        return;
      }
      // Капле без кнопки уходить некуда — при закрытии она гаснет (правило в `ui.css`).
      if (!size) layer.dataset.seed = "point";
      const [w, h] = size ?? [SEED, SEED];
      box.style.setProperty("--grow-w0", `${Math.min(w, box.offsetWidth)}px`);
      box.style.setProperty("--grow-h0", `${Math.min(h, box.offsetHeight)}px`);
      box.dataset.go = "";
      show();
    };
    start();

    // Закрыли — содержимое гаснет сразу; открыли снова, пока уходило (повторный
    // щелчок: Radix закрывает и тут же открывает), — проявляется заново.
    const mo = new MutationObserver(() => {
      if (box.dataset.state === "closed") {
        clearTimeout(reveal);
        delete box.dataset.shown;
      } else show();
    });
    mo.observe(box, { attributes: true, attributeFilter: ["data-state"] });

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(reveal);
      mo.disconnect();
    };
  }, [from]);

  return <span ref={ref} className="pop-layer" aria-hidden />;
}
